import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Calendar } from 'lucide-react';
import { getInjuries, getIllnesses, getAppointments } from '../../api';

// RTP removed (confirmed 2026-07-28) — RTC is now the final/closing stage.
const STAGES = [
  { id: 'medical_rehab', label: 'Medical Rehab', color: '#ef4444' },
  { id: 'ofr',           label: 'OFR',           color: '#f59e0b' },
  { id: 'rtt',           label: 'RTT',            color: '#8b5cf6' },
  { id: 'rtc',           label: 'RTC',            color: '#10b981' },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' }) + ' · ' +
    dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function StageBar({ current }) {
  const idx = STAGES.findIndex(s => s.id === current);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {STAGES.map((s, i) => {
          const active = s.id === current;
          const done   = i < idx;
          return (
            <React.Fragment key={s.id}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%', margin: '0 auto 4px',
                  background: active ? s.color : done ? '#334155' : 'var(--border)',
                  border: active ? `2px solid ${s.color}` : 'none',
                  boxShadow: active ? `0 0 0 4px ${s.color}33` : 'none',
                }} />
                <div style={{ fontSize: 9, color: active ? s.color : 'var(--text-dimmed)', fontWeight: active ? 700 : 400 }}>{s.label}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div style={{ flex: 0.5, height: 2, background: done ? '#334155' : 'var(--border)', marginBottom: 14 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 };

export default function PlayerMedical({ playerRecord }) {
  const navigate = useNavigate();
  const [injury,   setInjury]   = useState(null);
  const [illness,  setIllness]  = useState(null);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerRecord?.id) { setLoading(false); return; }
    const pid = playerRecord.id;

    Promise.all([
      getInjuries(pid),
      getIllnesses(pid),
      getAppointments(pid),
    ]).then(([inj, ill, appts]) => {
      const activeInjury = inj.find(i => !i.is_closed) || null;
      const activeIllness = ill.find(i => i.status !== 'cleared') || null;
      const now = new Date();
      const upcoming = appts
        .filter(a => new Date(a.appointment_date) >= now && a.status === 'scheduled')
        .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
      setInjury(activeInjury);
      setIllness(activeIllness);
      setUpcomingAppts(upcoming);
    }).finally(() => setLoading(false));
  }, [playerRecord]);

  if (loading) {
    return (
      <div style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ padding: '14px 14px 24px' }}>
      <button onClick={() => navigate('/player/me')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Medical</div>

      {/* Active injury */}
      {injury ? (
        <div style={CARD}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Active injury</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{injury.body_part}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{injury.injury_type}</div>
          <StageBar current={injury.current_stage} />
          {injury.expected_return_date && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#f59e0b' }}>
              Expected return: {fmtDate(injury.expected_return_date)}
            </div>
          )}
        </div>
      ) : illness ? null : (
        <div style={{ ...CARD, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>All clear ✓</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>No active injury or illness.</div>
        </div>
      )}

      {/* Active illness */}
      {illness && (
        <div style={CARD}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Active illness</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{illness.illness_type}</div>
          <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: illness.status === 'recovering' ? '#f59e0b' : '#ef4444' }}>{illness.status}</span>
            {illness.contagious && <span style={{ fontSize: 11, color: '#ef4444' }}>⚠ Contagious</span>}
          </div>
          {illness.expected_return_date && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#f59e0b' }}>
              Expected return: {fmtDate(illness.expected_return_date)}
            </div>
          )}
        </div>
      )}

      {/* All clear when only illness was blocking */}
      {!injury && !illness && null}

      {/* Upcoming appointments */}
      {upcomingAppts.length > 0 && (
        <div style={CARD}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Upcoming appointments</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcomingAppts.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Calendar size={16} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{a.appointment_type}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {fmtDateTime(a.appointment_date)}{a.location ? ` · ${a.location}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!injury && !illness && upcomingAppts.length === 0 && (
        <div style={{ ...CARD, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>All clear ✓</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>No active injury, illness, or upcoming appointments.</div>
        </div>
      )}
    </div>
  );
}
